import { useLocation, useNavigate, type NavigateFunction } from "react-router-dom";
import { useRoutes } from "react-router-dom";
import { createElement, Fragment, lazy, Suspense, useEffect, useRef } from "react";
import routes from "./config";
import { FloatingActions } from '../components/ui/FloatingActions';
// Z'eki dock'u ayrı parça: motion kütüphanesi (~45 KB gz) ana paketten çıkar, dock ayar isteği dönene kadar zaten
// boş çizildiğinden gecikme görünmez (2 Kasım).
const AssistantDock = lazy(() => import('../components/assistant/AssistantDock').then((m) => ({ default: m.AssistantDock })));

/** Sabit navigasyon çubuğunun yüksekliği (tailwind: h-nav). */
const NAV_OFFSET = 76;

let navigateResolver: (navigate: ReturnType<typeof useNavigate>) => void;

declare global {
  interface Window {
    REACT_APP_NAVIGATE: ReturnType<typeof useNavigate>;
    /** Meta Pixel (index.html'deki kod tanımlar; engellenmişse yoktur). */
    fbq?: (...args: unknown[]) => void;
  }
}

export const navigatePromise = new Promise<NavigateFunction>((resolve) => {
  navigateResolver = resolve;
});

export function AppRoutes() {
  const element = useRoutes(routes);
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();

  // `/fiyatlar/` → `/fiyatlar`: ön-çizilmiş sayfaları klasör olarak sunan sunucu adrese eğik çizgi ekleyebilir;
  // tek kanonik adres kalsın. Efektte yapılır: ilk çizim ön-çizilmiş HTML'le aynı kalır, sonra adres sessizce düzelir.
  useEffect(() => {
    if (pathname.length > 1 && pathname.endsWith('/')) {
      navigate(`${pathname.replace(/\/+$/, '')}${search}${hash}`, { replace: true });
    }
  }, [pathname, search, hash, navigate]);

  /*
   * Kaydırma yönetimi.
   *
   * Çapasız adres: başa dön. Tarayıcı SPA gezinmesinde kaydırmayı koruyor,
   * yeni sayfa önceki sayfanın kaydırma konumunda açılıyordu. `instant`,
   * kök öğedeki scroll-behavior: smooth yüzünden — geçişte yukarı doğru
   * uzun bir animasyon oynuyordu.
   *
   * Çapalı adres (ör. /#erken-erisim): hedefe kaydır. Tarayıcının kendi
   * çapa davranışı yalnız ilk yüklemede ve React içeriği basmadan önce
   * çalışıyor; alt sayfadan gelen bağlantı ana sayfayı en üstte açıyordu.
   */
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, behavior: 'instant' });
      return;
    }
    const target = document.querySelector(hash);
    if (!target) return;

    // NAV_OFFSET: sabit navigasyon çubuğu bölümün üstünü örtüyor.
    // Yumuşak kaydırma uzun mesafede yarıda kesiliyordu, `instant`.
    const scroll = () => {
      const top = target.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
      window.scrollTo({ top, behavior: 'instant' });
    };

    // Bir kare beklenir: hedefin yeri ancak düzen kurulduktan sonra doğru.
    // Yükleme sürerken tarayıcı kaydırmayı kendi sıfırlıyor; tam sayfa
    // yenilemesinde (alt sayfadan gelen bağlantı) yükleme bitince tekrarlanır.
    const frame = requestAnimationFrame(scroll);
    if (document.readyState !== 'complete') {
      window.addEventListener('load', scroll, { once: true });
    }

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('load', scroll);
    };
  }, [pathname, hash]);
  useEffect(() => {
    window.REACT_APP_NAVIGATE = navigate;
    navigateResolver(window.REACT_APP_NAVIGATE);
  });

  /*
   * Meta Pixel: index.html'deki kod yalnız ilk yüklemede PageView gönderir; router'la açılan her yeni
   * sayfa için burada tekrar gönderilir. İlk çizim (zaten sayıldı) ve sondaki eğik çizgi düzeltmesi
   * (`/fiyatlar/` → `/fiyatlar`, aynı sayfa) sayılmaz; çapa/sorgu değişimi de sayılmaz. GA4 geçmiş
   * olaylarını "gelişmiş ölçüm" ile kendi izlediği için burada yalnız Pixel.
   */
  const lastTrackedPath = useRef<string | null>(null);
  useEffect(() => {
    const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
    if (lastTrackedPath.current === null) {
      lastTrackedPath.current = normalized;
      return;
    }
    if (lastTrackedPath.current === normalized) return;
    lastTrackedPath.current = normalized;
    window.fbq?.('track', 'PageView');
  }, [pathname]);

  // Yüzen öğeler (başa dön, Z'eki sohbet hapı) her sayfada; sohbet durumu rotalar değişince korunur, çünkü dock rotaların dışında
  // bir kez bağlanır (sunucu çiziminde de aynı ağaç: entry-server AppRoutes'u kullanır).
  // Sayfalar ve dock `lazy` (config.tsx): Suspense sınırı parça gelene kadar ön-çizilmiş HTML'i ekranda tutar;
  // yedek `null` yalnız sayfa içi geçişte ilk kez açılan sayfada bir an görünür.
  return createElement(
    Fragment,
    null,
    createElement(Suspense, { fallback: null }, element),
    createElement(FloatingActions),
    createElement(Suspense, { fallback: null }, createElement(AssistantDock))
  );
}
